import React, { useState, useEffect } from 'react';
import { GripVertical, Trash2 } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useTrainingPlanStore from '../../stores/trainingPlanStore';
import { useTheme } from '../../context/ThemeContext';

export default function ExerciseAccordion({ exerciseId, workoutId }) {
 const { themeClasses } = useTheme();
 const exercise = useTrainingPlanStore(state => state.exercises[exerciseId]);
 const sets = useTrainingPlanStore(state => state.sets);
 const isActive = useTrainingPlanStore(state => state.ui.expandedExerciseId === exerciseId);
 const openExerciseSettings = useTrainingPlanStore(state => state.openExerciseSettings);
 const removeExercise = useTrainingPlanStore(state => state.removeExercise);

 const language = localStorage.getItem('language') || 'english';
 const he = language === 'hebrew';

 const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
 id: exerciseId,
 data: { type: 'exercise', id: exerciseId, workoutId }
 });

 const style = {
 transform: CSS.Transform.toString(transform),
 transition,
 zIndex: isDragging ? 10 : 1,
 opacity: isDragging ? 0.8 : 1
 };

 if (!exercise) return null;

 const firstSet = exercise.setIds?.[0] ? sets[exercise.setIds[0]] : null;
 const repsLabel = firstSet
 ? (firstSet.min_reps === firstSet.max_reps
 ? `${firstSet.min_reps}`
 : `${firstSet.min_reps}–${firstSet.max_reps}`)
 : '—';

 return (
 <div
 ref={setNodeRef}
 style={style}
 className={`rounded-md border overflow-hidden ${
 isDragging ? 'border-forest ' : isActive
 ? 'border-forest bg-forest/10 dark:bg-forest/20'
 : 'border-line dark:border-line-dark bg-surface dark:bg-surface-dark'
 } transition-[transform,opacity] duration-200`}
 >
 <div className="flex items-center gap-1 min-h-[56px] px-1.5 sm:px-2">
 <button
 type="button"
 {...attributes}
 {...listeners}
 className="min-h-11 min-w-11 inline-flex items-center justify-center cursor-grab active:cursor-grabbing text-ink-muted hover:text-ink-muted dark:hover:text-ink-muted-dark"
 aria-label={he ? 'גרור' : 'Drag'}
 onClick={(e) => e.stopPropagation()}
 >
 <GripVertical size={18} />
 </button>

 <button
 type="button"
 onClick={() => openExerciseSettings(exerciseId)}
 className="flex-1 min-h-11 min-w-0 px-2 text-start rounded-md hover:bg-forest/10 dark:hover:bg-forest/20 transition-colors duration-120 outline-none focus-visible:ring-2 focus-visible:ring-forest"
 >
 <h4 className={`font-bold text-[15px] truncate ${themeClasses.textPrimary}`}>
 {exercise.exercise_name || (he ? 'תרגיל' : 'Exercise')}
 </h4>
 <p className="mt-0.5 text-xs font-medium text-ink-muted">
 {exercise.setIds?.length || 0} {he ? 'סטים' : 'sets'}
 <span className="mx-1.5 opacity-40">·</span>
 {repsLabel} {he ? 'חזרות' : 'reps'}
 <span className="mx-1.5 opacity-40">·</span>
 {exercise.rest_seconds || 0}s {he ? 'מנוחה' : 'rest'}
 </p>
 </button>

 <button
 type="button"
 onClick={() => removeExercise(workoutId, exerciseId)}
 className="min-h-11 min-w-11 inline-flex items-center justify-center rounded-lg text-ink-muted hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-opacity"
 aria-label={he ? 'מחק תרגיל' : 'Remove exercise'}
 >
 <Trash2 size={18} />
 </button>
 </div>
 </div>
 );
}
